import { zodResolver } from "@hookform/resolvers/zod";
import { Info } from "lucide-react";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import styles from "./Base.module.scss";

import {
  BudgetFormData,
  useClientMonthlyBudgetSchema,
} from "@/components/forms/budgetPlanning/BudgetFormTypes";
import Icons from "@/components/icons";
import { Alert, AlertDescription } from "@/components/ui/v2/alert";
import { Button } from "@/components/ui/v2/button";
import { useUpdateCampaignJobBoardMonthBudgetMutation } from "@/features/campaigns/jobboards/api/useUpdateCampaignJobBoardMonthBudgetMutation";
import BudgetForm from "@/features/transactions/components/BudgetForm";
import { usePopup } from "@/lib/hooks/usePopup";
import useToast from "@/lib/hooks/useToast";

interface EditForecastBudgetPopupProps {
  budget: number;
  campaignId: string;
  clientId: string;
  clientIntegrationModuleId: string;
  year: string;
  month: string;
  onCancel: () => void;
  onSuccess: () => void;
}

export const EditForecastBudgetPopup = ({
  budget,
  campaignId,
  clientId,
  clientIntegrationModuleId,
  year,
  month,
  onCancel,
  onSuccess,
}: EditForecastBudgetPopupProps) => {
  const { disableClose, enableClose } = usePopup();
  const { showToast } = useToast();
  const { t } = useTranslation();
  const updateMonthBudget = useUpdateCampaignJobBoardMonthBudgetMutation();
  const { clientMonthlyBudgetSchema } = useClientMonthlyBudgetSchema();

  const methods = useForm<BudgetFormData>({
    resolver: zodResolver(clientMonthlyBudgetSchema),
    mode: "onChange",
    defaultValues: {
      budget: budget || 0,
    },
  });

  const handleSubmit = (data: BudgetFormData) => {
    updateMonthBudget.mutate(
      {
        amount: data.budget,
        campaignId,
        clientId,
        clientIntegrationModuleId,
        year,
        month: month.padStart(2, "0"),
      },
      {
        onSuccess: () => {
          showToast(t("success.updated", { entity: t("common.forecast") }), "success");
          onSuccess();
        },
      }
    );
  };

  useEffect(() => {
    if (updateMonthBudget.isPending) {
      disableClose();
    } else {
      enableClose();
    }
  }, [updateMonthBudget.isPending, disableClose, enableClose]);

  const errorCode = updateMonthBudget.error?.response?.data?.title;

  return (
    <>
      <div className={styles.header}>
        <h3 className={styles.title}>
          {t("actions.edit", { entity: t("common.forecast") })}
        </h3>
        <Alert variant="default">
          <Info className="size-4" />
          <AlertDescription>
            {t("This will only change the selected month budget")}
          </AlertDescription>
        </Alert>
      </div>
      <BudgetForm
        isLoading={updateMonthBudget.isPending}
        onSubmit={handleSubmit}
        methods={methods}
        error={errorCode && t(`errorCodes.${errorCode}`)}
      />
      <div className={styles.actions}>
        <Button variant="outline" onClick={onCancel}>
          {t("common.cancel")}
        </Button>
        <Button
          loading={updateMonthBudget.isPending}
          onClick={methods.handleSubmit(handleSubmit)}
        >
          <Icons.AddIcon fontSize={24} />
          {t("actions.save", { entity: t("common.forecast") })}
        </Button>
      </div>
    </>
  );
};
